import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  AuthLoginResponse,
  AuthMeResponse,
  ChangePasswordBody,
  ForgotPasswordBody,
  LoginBody,
  OkResponse,
  PasswordResetRequestResponse,
  RegisterBody,
  RegisterResponse,
  ResendVerificationBody,
  ResetPasswordBody,
  VerifyEmailBody,
  VerifyEmailResponse,
} from '@/types/backend'
import { useStore } from '@/store/Store'

export const useOwnerRegisterMutation = () => {
  return useMutation({
    mutationFn: async (payload: RegisterBody) => {
      const { data } = await apiClient.post<RegisterResponse>(
        `/auth/register`,
        payload
      )
      return data
    },
  })
}

// ==== VERIFY EMAIL (logs the user in, mirrors what register used to do)
export const useVerifyEmailMutation = () => {
  const { dispatch } = useStore()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: VerifyEmailBody) => {
      const { data } = await apiClient.post<VerifyEmailResponse>(
        `/auth/verify-email`,
        payload
      )
      return data
    },
    onSuccess: (data) => {
      const auth = data as AuthLoginResponse
      localStorage.setItem('userInfo', JSON.stringify(auth))
      dispatch({ type: 'USER_SIGNIN', payload: auth })
      qc.setQueryData(['me'], { user: auth.user })
    },
  })
}

// ==== RESEND VERIFICATION
export const useResendVerificationMutation = () => {
  return useMutation({
    mutationFn: async (payload: ResendVerificationBody) => {
      const { data } = await apiClient.post<OkResponse>(
        `/auth/resend-verification`,
        payload
      )
      return data
    },
  })
}

// ==== LOGIN
export const useLoginMutation = () => {
  const { dispatch } = useStore()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: LoginBody) => {
      const { data } = await apiClient.post<AuthLoginResponse>(
        `/auth/login`,
        payload
      )
      return data
    },
    onSuccess: (data) => {
      localStorage.setItem('userInfo', JSON.stringify(data))
      dispatch({ type: 'USER_SIGNIN', payload: data })
      qc.setQueryData<AuthMeResponse>(['me'], { user: data.user })
    },
  })
}

//==== ME
export const useMeQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['me'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<AuthMeResponse>(`/auth/me`)
      return data
    },
  })
}

//=== CHANGE PASSWORD
export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: async (payload: ChangePasswordBody) => {
      const { data } = await apiClient.patch<OkResponse>(
        `auth/change-password`,
        payload
      )
      return data
    },
  })
}

//=== FORGOT PASSWORD
export const useForgotPasswordMutation = () => {
  return useMutation({
    mutationFn: async (payload: ForgotPasswordBody) => {
      const { data } = await apiClient.post<PasswordResetRequestResponse>(
        `/auth/forgot-password`,
        payload
      )
      return data
    },
  })
}

//=== RESET PASSWORD
export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: async (payload: ResetPasswordBody) => {
      const { data } = await apiClient.post<OkResponse>(
        `/auth/reset-password`,
        payload
      )
      return data
    },
  })
}

//=== LOGOUT helper
export const useLogout = () => {
  const { dispatch } = useStore()
  return () => {
    localStorage.removeItem('userInfo')
    dispatch({ type: 'USER_SIGNOUT' })
  }
}
